/**
 * Where a place search is centred.
 *
 * The centre is not decoration: Goong, the Vietnamese index, will not answer without one, and the same
 * input asked from nowhere can come back with an unrelated place in another province. Measured — the
 * reason `near` is a required input on that provider rather than an optional hint.
 *
 * Two rules, both about not inventing a place:
 *
 * - **The first point the caller actually has wins.** A pin the user placed, or a coordinate a screen
 *   handed over, is a fact; it is preferred over the reference destination.
 * - **The reference destination is a search bias, never a stored coordinate.** It exists because the
 *   first release is a single reference destination — the same constant the map picker opens on — and
 *   without it a user who has typed nothing yet would search from nowhere. It is passed to the provider
 *   as a ranking hint and is never written to a place.
 */

/** The reference destination. Every place in the first release is here, so a fresh search is centred here. */
export const REFERENCE_CENTRE = { lat: 11.9404, lon: 108.4583 } as const;

/**
 * The first known point, or the reference destination.
 *
 * Variadic rather than a fixed pair, because the callers differ: the capture screen has a pin and a
 * viewport to choose between, the quick-capture bar has neither.
 */
export function searchCentre(
  ...candidates: ReadonlyArray<{ lat: number; lon: number } | null>
): { lat: number; lon: number } {
  for (const candidate of candidates) {
    if (candidate !== null) return { lat: candidate.lat, lon: candidate.lon };
  }
  return { lat: REFERENCE_CENTRE.lat, lon: REFERENCE_CENTRE.lon };
}
